import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'
import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useAgencyMarketing from '@hooks/useAgencyMarketing'

import PageContext from '@contexts/pageContext'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { TemplatePageCategoryModel } from '@models/types'

import TemplateGroup from '../TemplateGroup'
import TemplatesTools from '../TemplatesTools'

const buildPageFilterFields = (availableCategories: TemplatePageCategoryModel[]) => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'category_id',
      label: 'Filter by Category',
      values: availableCategories.map((category: TemplatePageCategoryModel) => ({
        key: category?.id,
        label: category?.title,
      })),
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { tagId } = parsePermittedQueryParams(getQueryParamsFromHash(), ['tagId'])

  const agencyMarketingPayload = useAgencyMarketing({
    filters: {
      // @ts-expect-error TS2322
      tag_id: tagId,
      ...pageFilters,
    },
  })

  const {
    availableCategories,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePagesCount,
    groupedTemplatePages,
    hasGroupedTemplatePages,
    loading,
    urls,
    urls: {
      agencyMarketingSelectTemplateUrl,
    },
  } = agencyMarketingPayload

  return (
    <>
      <Helmet>
        <title>Agency Marketing Templates | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="templates"
        tabBarItems={[
          {
            href: agencyMarketingSelectTemplateUrl,
            icon: faHome,
            key: 'templates',
            title: 'Templates',
          },
        ]}
        title="Agency Marketing"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyMarketing.templates.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && hasGroupedTemplatePages && (groupedTemplatePages.map(group => (
              // @ts-expect-error TS18046
              <TemplateGroup key={group.id} agencyMarketingPayload={agencyMarketingPayload} templateGroup={group} />
            )))}

            {!loading && canLoadMore && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyMarketing.templates.sidebar" />

            <TemplatesTools urls={urls} />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(availableCategories)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
